import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, Chapter } from '../services/api';
import { Course, Section } from '../types';
import ContentViewer from '../components/ContentViewer';
import { 
  Loader2, ArrowLeft, Save, Eye, EyeOff, Sparkles, 
  Send, Edit3, RotateCcw, Plus, ChevronDown, ChevronRight, FileText,
  Trash2, RefreshCw
} from 'lucide-react';

// Parse course outline from HTML content
const parseOutline = (course: Course): Section[] => {
  if (course.outline) {
    const lines = course.outline.split('\n').filter(l => l.trim());
    const sections: Section[] = [];
    let currentSection: Section | null = null;
    let sectionOrder = 0;
    let subOrder = 0;
    
    for (const line of lines) {
      const sectionMatch = line.match(/^Section\s+(\d+):\s*(.+)/i);
      const subMatch = line.match(/^(\d+\.\d+)\s+(.+)/);
      
      if (sectionMatch) {
        currentSection = {
          id: `section-${sectionMatch[1]}`,
          title: sectionMatch[2],
          order: sectionOrder++,
          subsections: [],
        };
        sections.push(currentSection);
        subOrder = 0;
      } else if (subMatch && currentSection) {
        currentSection.subsections.push({
          id: `section-${subMatch[1]}`,
          title: subMatch[2],
          order: subOrder++,
        });
      }
    }
    
    if (sections.length > 0) return sections;
  }
  
  // Fallback: extract from HTML
  if (course.content) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(course.content, 'text/html');
    const headings = doc.querySelectorAll('h1, h2');
    
    const sections: Section[] = [];
    let currentSection: Section | null = null;
    let order = 0;
    
    headings.forEach((heading, idx) => {
      const level = heading.tagName === 'H1' ? 1 : 2;
      const title = heading.textContent || '';
      const container = heading.closest('section, article') as HTMLElement | null;
      const id = container?.id || (heading as HTMLElement).id || `section-${idx}`;
      
      if (level === 1) {
        currentSection = {
          id,
          title,
          order: sections.length,
          subsections: [],
        };
        sections.push(currentSection);
      } else if (currentSection) {
        currentSection.subsections.push({
          id,
          title,
          order: order++,
        });
      }
    });
    
    if (sections.length > 0) return sections;
  }
  
  // Default fallback
  return [{
    id: 'section-1',
    title: course.title || 'Course Content',
    order: 0,
    subsections: [{ id: 'section-1-1', title: 'Introduction', order: 0 }],
  }];
};

const getSectionHtml = (html: string, sectionId: string): string => {
  if (!html || !sectionId) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const element = doc.getElementById(sectionId);
  if (!element) return '';
  const container = element.closest('section, article') as HTMLElement | null;
  return (container || element).outerHTML;
};

export default function EditCourse() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [activeChapter, setActiveChapter] = useState<Chapter | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSection, setCurrentSection] = useState<string>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [editInstructions, setEditInstructions] = useState('');
  const [newSectionOutline, setNewSectionOutline] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<{step: string, message: string, progress: number} | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'preview' | 'edit' | 'ai'>('preview');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [pendingDraft, setPendingDraft] = useState<{ id: number; content: string } | null>(null);
  const assetBase = process.env.PUBLIC_URL || '';

  useEffect(() => {
    if (courseId) {
      loadCourse(parseInt(courseId));
    }
  }, [courseId]);

  const loadCourse = async (id: number) => {
    try {
      const [data, chaptersData] = await Promise.all([
        api.getTeacherCourse(id),
        api.getChapters(id)
      ]);
      
      setCourse(data);
      setTitle(data.title);
      setDescription(data.description || '');
      
      // Sort chapters by chapter_number
      const sortedChapters = chaptersData.sort((a, b) => a.chapter_number - b.chapter_number);
      setChapters(sortedChapters);
      
      // Set active chapter and its content
      if (sortedChapters.length > 0) {
        const firstChapter = sortedChapters[0];
        setActiveChapter(firstChapter);
        setContent(firstChapter.content || data.content || '');
        
        // Parse outline from chapter content
        const parsedSections = parseOutline({...data, content: firstChapter.content || ''});
        setSections(parsedSections);
        setExpandedSections(new Set(parsedSections.map(s => s.id)));
        
        if (parsedSections.length > 0 && parsedSections[0].subsections.length > 0) {
          setCurrentSection(parsedSections[0].subsections[0].id);
        }
      } else {
        setContent(data.content || '');
        const parsedSections = parseOutline(data);
        setSections(parsedSections);
        setExpandedSections(new Set(parsedSections.map(s => s.id)));
      }
    } catch (error) {
      console.error('Failed to load course:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChapterSelect = (chapter: Chapter) => {
    setActiveChapter(chapter);
    setContent(chapter.content || '');
    setPendingDraft(null);
    
    // Re-parse outline for the new chapter
    if (course) {
      const parsedSections = parseOutline({...course, content: chapter.content || ''});
      setSections(parsedSections);
      setExpandedSections(new Set(parsedSections.map(s => s.id)));
    }
  };

  const statusLabel = (status: Chapter['status']) => {
    if (status === 'published') return 'Published';
    if (status === 'draft_ready') return 'Draft ready';
    if (status === 'generating') return 'Generating';
    if (status === 'generation_failed') return 'Generation failed';
    if (status === 'generated') return 'Generated';
    return 'Draft';
  };

  const handleSave = async () => {
    if (!courseId) return;
    
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.updateCourse(parseInt(courseId), { title, description });
      if (activeChapter) {
        await api.saveChapterContent(activeChapter.id, content);
      } else {
        await api.updateCourse(parseInt(courseId), { content });
      }
      await loadCourse(parseInt(courseId));
      setSuccess(activeChapter ? 'Chapter saved as teacher draft. Use Sync to publish it to students.' : 'Course saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save course');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAIEdit = async () => {
    if (!activeChapter || !editInstructions.trim()) return;
    
    setIsEditing(true);
    setError('');

    try {
      const selectedHtml = getSectionHtml(content, currentSection);
      const draft = await api.editChapterContent(
        activeChapter.id,
        content,
        editInstructions,
        currentSection || undefined,
        selectedHtml || undefined
      );

      setPendingDraft({ id: draft.draft_id, content: draft.content });
      setContent(draft.content);
      setActiveTab('preview');
      setSuccess('AI edit preview generated. Review it, then save or discard.');
      setEditInstructions('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      let errorMsg = 'Failed to edit content';
      if (err.response?.status === 502) {
        errorMsg = 'AI generation timed out (502). Please try again with simpler instructions.';
      } else if (err.code === 'ECONNABORTED') {
        errorMsg = 'Request timed out. The AI is taking too long. Please try again.';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setError(errorMsg);
    } finally {
      setIsEditing(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!pendingDraft || !courseId) return;
    setIsSaving(true);
    setError('');
    try {
      await api.saveDraft(pendingDraft.id);
      setPendingDraft(null);
      await loadCourse(parseInt(courseId));
      setSuccess('Draft saved and published to student view');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardDraft = () => {
    setPendingDraft(null);
    setContent(activeChapter?.content || course?.content || '');
    setSuccess('');
  };

  const handleGenerateSection = async () => {
    if (!courseId || !newSectionOutline.trim()) return;
    
    setIsGenerating(true);
    setError('');
    setGenerationStatus({ step: 'starting', message: 'Preparing generation...', progress: 0 });

    try {
      // Parse the outline to extract chapter title and sections
      const lines = newSectionOutline.trim().split('\n').filter(l => l.trim());
      const chapterTitle = lines[0] || 'New Chapter';
      const chapterNumber = chapters.length + 1;
      
      setGenerationStatus({ step: 'planning', message: 'Creating new chapter...', progress: 10 });
      
      // Create new chapter first
      const newChapter = await api.createChapter(parseInt(courseId), {
        chapter_number: chapterNumber,
        title: chapterTitle,
        order: chapterNumber,
        outline: newSectionOutline,
      });
      
      setGenerationStatus({ step: 'generating', message: 'Generating content with AI...', progress: 30 });
      
      // Generate content for the new chapter
      const draft = await api.generateChapterContent(
        newChapter.id,
        newSectionOutline,
        undefined
      );
      
      setGenerationStatus({ step: 'finalizing', message: 'Saving content...', progress: 80 });
      
      // Save the draft
      await api.saveDraft(draft.draft_id);
      
      setGenerationStatus({ step: 'complete', message: 'Generation complete!', progress: 100 });
      setSuccess(`Chapter ${chapterNumber} generated successfully`);
      setNewSectionOutline('');
      
      // Reload to get new chapter
      setTimeout(() => {
        loadCourse(parseInt(courseId));
        setGenerationStatus(null);
      }, 2000);
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      let errorMsg = 'Failed to generate section';
      if (err.response?.status === 502) {
        errorMsg = 'Generation timed out (502). The AI is taking too long to respond. Please try again with a shorter outline or try later.';
      } else if (err.response?.status === 504) {
        errorMsg = 'Generation timed out (504). Please try again.';
      } else if (err.code === 'ECONNABORTED') {
        errorMsg = 'Request timed out. The AI generation is taking longer than expected. Please try again.';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setError(errorMsg);
      setGenerationStatus(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!activeChapter || !courseId) return;
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      if (activeChapter.status === 'published') {
        await api.unpublishChapter(activeChapter.id);
        setSuccess(`Chapter ${activeChapter.chapter_number} unpublished from student view.`);
      } else {
        await api.publishChapter(activeChapter.id);
        setSuccess(`Chapter ${activeChapter.chapter_number} published to student view.`);
      }
      await loadCourse(parseInt(courseId));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update chapter publish status');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteChapter = async () => {
    if (!activeChapter || !courseId) return;
    const ok = window.confirm(`Delete Chapter ${activeChapter.chapter_number}: ${activeChapter.title}? This cannot be undone.`);
    if (!ok) return;

    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.deleteChapter(activeChapter.id);
      await loadCourse(parseInt(courseId));
      setSuccess('Chapter deleted and student content synced');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete chapter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncCourse = async () => {
    if (!activeChapter || !courseId) return;
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.syncChapterContent(activeChapter.id);
      await loadCourse(parseInt(courseId));
      setSuccess(`Chapter ${activeChapter.chapter_number} published to student view. ${result.chapters_synced} chapters are currently visible.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to sync chapter');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const selectSection = (sectionId: string, sectionTitle: string) => {
    setCurrentSection(sectionId);
    setEditInstructions(`Please review and improve the content for "${sectionTitle}". You can:
- Add more detailed explanations
- Include code examples
- Add visualizations or diagrams
- Create practice exercises
- Improve clarity and flow`);
    setActiveTab('ai');
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-apple-blue animate-spin" />
          <p className="text-apple-text-secondary">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-apple-text mb-2">Course not found</h2>
          <button
            onClick={() => navigate('/teacher')}
            className="btn-primary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-apple-border bg-white">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/teacher')}
            className="flex items-center gap-2 text-apple-text-secondary hover:text-apple-text"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="w-px h-6 bg-apple-border" />
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-lg font-semibold text-apple-text bg-transparent border-none focus:outline-none focus:ring-0 min-w-[300px]"
          />
        </div>

        <div className="flex items-center gap-3">
          {success && (
            <span className="text-sm text-green-600">{success}</span>
          )}
          {error && (
            <span className="text-sm text-red-600">{error}</span>
          )}
          {pendingDraft && (
            <>
              <button
                onClick={handleDiscardDraft}
                className="btn-outline flex items-center gap-2 text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                Discard Preview
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save AI Draft
              </button>
            </>
          )}
          
          <button
            onClick={handleTogglePublish}
            disabled={isSaving || !activeChapter}
            className={`btn-outline flex items-center gap-2 text-sm ${
              activeChapter?.status === 'published' ? 'text-apple-green' : ''
            }`}
          >
            {activeChapter?.status === 'published' ? (
              <>
                <EyeOff className="w-4 h-4" />
                Unpublish
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Publish
              </>
            )}
          </button>

          <button
            onClick={handleSyncCourse}
            disabled={isSaving || !activeChapter}
            className="btn-outline flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Sync
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving || !!pendingDraft || !activeChapter}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>

          {activeChapter && (
            <button
              onClick={handleDeleteChapter}
              disabled={isSaving}
              className="btn-outline flex items-center gap-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete Chapter
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 py-2 border-b border-apple-border bg-apple-bg">
        {[
          { id: 'preview', label: 'Preview', icon: Eye },
          { id: 'edit', label: 'Edit HTML', icon: Edit3 },
          { id: 'ai', label: 'AI Assistant', icon: Sparkles },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-apple-text shadow-sm'
                : 'text-apple-text-secondary hover:text-apple-text'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Sidebar - Chapters & Outline */}
        <div className="w-72 bg-apple-card border-r border-apple-border flex flex-col">
          {/* Chapter Selector */}
          {chapters.length > 0 && (
            <div className="px-4 py-3 border-b border-apple-border bg-apple-bg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <img src={`${assetBase}/static/polyu-icon.png`} alt="PolyU" className="w-5 h-5" />
                  <span className="text-sm font-medium text-apple-text">Chapter ({chapters.length} total)</span>
                </div>
              </div>
              <select
                value={activeChapter?.id || ''}
                onChange={(e) => {
                  const chapter = chapters.find(c => c.id === parseInt(e.target.value));
                  if (chapter) handleChapterSelect(chapter);
                }}
                className="w-full text-sm border border-apple-border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-apple-blue"
              >
                {chapters.map((chapter) => (
                  <option key={chapter.id} value={chapter.id}>
                    Ch {chapter.chapter_number}: {chapter.title} [{statusLabel(chapter.status)}]
                  </option>
                ))}
              </select>
              <p className="text-xs text-apple-text-secondary mt-2">
                Teachers can view published chapters and latest generated drafts.
              </p>
              {activeChapter && (
                <div className="mt-3 flex items-center justify-between rounded-lg bg-white border border-apple-border px-3 py-2">
                  <span className="text-xs text-apple-text-secondary">Status</span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    activeChapter.status === 'published' ? 'bg-green-50 text-green-700' :
                    activeChapter.status === 'draft_ready' ? 'bg-blue-50 text-blue-700' :
                    activeChapter.status === 'generating' ? 'bg-amber-50 text-amber-700' :
                    activeChapter.status === 'generation_failed' ? 'bg-red-50 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {statusLabel(activeChapter.status)}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="px-4 py-4 border-b border-apple-border">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-apple-text">Course Outline</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/teacher/generate/${courseId}`)}
                  className="text-xs bg-apple-blue text-white px-2 py-1 rounded hover:bg-apple-blue/80 transition-colors"
                >
                  + New Chapter
                </button>
                <button
                  onClick={() => setActiveTab('ai')}
                  className="text-xs text-apple-blue hover:underline"
                >
                  AI Edit
                </button>
              </div>
            </div>
            <p className="text-xs text-apple-text-secondary">
              Click a section to edit with AI
            </p>
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            {sections.map((section) => (
              <div key={section.id} className="mb-1">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-apple-gray transition-colors"
                >
                  {expandedSections.has(section.id) ? (
                    <ChevronDown className="w-4 h-4 text-apple-text-secondary" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-apple-text-secondary" />
                  )}
                  <span className="font-medium text-apple-text text-sm">{section.title}</span>
                </button>

                {expandedSections.has(section.id) && (
                  <div className="ml-4">
                    {section.subsections.map((subsection) => (
                      <button
                        key={subsection.id}
                        onClick={() => selectSection(subsection.id, subsection.title)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm transition-colors ${
                          currentSection === subsection.id
                            ? 'bg-apple-blue text-white'
                            : 'text-apple-text-secondary hover:bg-apple-gray hover:text-apple-text'
                        }`}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>{subsection.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'preview' && (
            <div className="h-full overflow-y-auto bg-white">
              <div className="max-w-4xl mx-auto px-8 py-8">
                <ContentViewer content={content} />
              </div>
            </div>
          )}

          {activeTab === 'edit' && (
            <div className="h-full flex flex-col">
              <div className="px-6 py-3 bg-slate-50 border-b border-apple-border flex items-center justify-between">
                <span className="text-sm text-apple-text-secondary">Edit HTML Content</span>
                <button
                  onClick={() => setContent(activeChapter?.content || course.content || '')}
                  className="text-sm text-apple-text-secondary hover:text-apple-text flex items-center gap-1"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="flex-1 p-6 font-mono text-sm resize-none focus:outline-none"
                spellCheck={false}
              />
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="h-full flex">
              {/* AI Panel */}
              <div className="w-[450px] border-r border-apple-border bg-apple-bg p-6 overflow-y-auto">
                <h3 className="font-semibold text-apple-text mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-apple-purple" />
                  AI Editor
                </h3>
                
                {/* Edit Current Section */}
                <div className="mb-6 p-4 bg-white rounded-apple-sm border border-apple-border">
                  <h4 className="font-medium text-apple-text mb-2 flex items-center gap-2">
                    <Edit3 className="w-4 h-4" />
                    Edit Current Content
                  </h4>
                  <p className="text-xs text-apple-text-secondary mb-3">
                    {currentSection 
                      ? `Editing: ${sections.find(s => s.subsections.some(sub => sub.id === currentSection))?.title} - ${sections.find(s => s.subsections.some(sub => sub.id === currentSection))?.subsections.find(sub => sub.id === currentSection)?.title}`
                      : 'Select a section from the left sidebar to edit'}
                  </p>
                  <textarea
                    value={editInstructions}
                    onChange={(e) => setEditInstructions(e.target.value)}
                    className="input min-h-[120px] resize-none mb-3"
                    placeholder="Describe what changes you want..."
                    rows={5}
                  />
                  <button
                    onClick={handleAIEdit}
                    disabled={!editInstructions.trim() || isEditing}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {isEditing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Editing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Apply Changes
                      </>
                    )}
                  </button>
                </div>

                {/* Generate New Section */}
                <div className="p-4 bg-white rounded-apple-sm border border-apple-border">
                  <h4 className="font-medium text-apple-text mb-2 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Generate New Section
                  </h4>
                  <p className="text-xs text-apple-text-secondary mb-3">
                    Provide an outline for a new chapter or section
                  </p>
                  <textarea
                    value={newSectionOutline}
                    onChange={(e) => setNewSectionOutline(e.target.value)}
                    className="input min-h-[150px] resize-none mb-3"
                    placeholder={`Example format:
第 2 章：数据与问题表达

2.1 现实问题 → 数据问题
2.2 基本概念：变量、样本、总体
2.3 数据类型：数值型 vs. 类别型
2.4 数据质量：缺失值、重复、异常
2.5 从问题到指标

【案例研究】：二手手机价格预测`}
                    rows={8}
                  />
                  <button
                    onClick={handleGenerateSection}
                    disabled={!newSectionOutline.trim() || isGenerating}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate Section
                      </>
                    )}
                  </button>
                  
                  {/* Generation Progress */}
                  {generationStatus && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-800">
                          {generationStatus.step === 'planning' && '📋 Planning...'}
                          {generationStatus.step === 'generating' && '✍️ Writing content...'}
                          {generationStatus.step === 'finalizing' && '🔧 Finalizing...'}
                          {generationStatus.step === 'complete' && '✅ Complete!'}
                        </span>
                        <span className="text-xs text-blue-600">{generationStatus.progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${generationStatus.progress}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-blue-700">{generationStatus.message}</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-apple-text mb-2">Quick Edit Templates</h4>
                  <div className="space-y-2">
                    {[
                      'Add more code examples',
                      'Simplify explanations',
                      'Add visual diagrams',
                      'Include practice exercises',
                      'Add quiz questions',
                      'Expand case study',
                    ].map((action) => (
                      <button
                        key={action}
                        onClick={() => setEditInstructions(action)}
                        className="w-full text-left px-3 py-2 text-sm text-apple-text-secondary hover:bg-white hover:text-apple-text rounded-lg transition-colors border border-transparent hover:border-apple-border"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="flex-1 overflow-y-auto bg-white">
                <div className="px-6 py-3 bg-slate-50 border-b border-apple-border flex items-center justify-between">
                  <span className="text-sm text-apple-text-secondary">Preview</span>
                  <span className="text-xs text-apple-text-secondary">
                    Total content: {content.length} characters
                  </span>
                </div>
                <div className="p-8">
                  <ContentViewer content={content} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
